import { useState, useEffect } from 'react';
import {
  ShoppingBag,
  ShoppingCart,
  Receipt,
  Search,
  Plus,
  Minus,
  Trash2,
  CheckCircle2,
  AlertCircle,
  FileText,
  Clock,
  ArrowRight,
  ChevronLeft,
  Eye,
  ExternalLink,
} from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { storeApiRequest, setSessionToken, getSessionToken, API_ORIGIN } from '../api/client.js';
import { useTelegram } from '../telegram/useTelegram.js';
import { useI18n } from '../context/I18nContext.js';
import { useCart } from '../hooks/useCart.js';
import { compressImageToBlob } from '../utils/compress.js';
import { formatMoneyMinor } from '@fencom/shared';
import { ModalDialog } from '../components/ModalDialog.js';
import { ImageDropzone } from '../components/ImageDropzone.js';
import { ProductImage } from '../components/ProductImage.js';

interface ShopInfo {
  id: string;
  slug: string;
  name: string;
  currency: string;
  status: string;
  deliveryFeeMinor: number;
}

interface Product {
  id: string;
  name: string;
  description?: string;
  status: string;
  coverImage?: string;
  priceMinor?: number;
  defaultVariantId?: string;
  variants?: Array<{
    id: string;
    name: string;
    priceMinor: number;
    stock?: number;
    isDefault?: number;
  }>;
}

interface OrderItem {
  id: string;
  productId?: string;
  variantId?: string;
  productName?: string;
  variantName?: string;
  productNameSnapshot?: string;
  variantNameSnapshot?: string;
  unitPriceMinor: number;
  quantity: number;
  lineTotalMinor: number;
}

interface Order {
  id: string;
  orderNumber: string | number;
  status: string;
  subtotalMinor?: number;
  deliveryFeeMinor?: number;
  discountMinor?: number;
  totalMinor: number;
  currency: string;
  couponCode?: string;
  customerNameSnapshot?: string;
  customerPhoneSnapshot?: string;
  addressSnapshotText?: string;
  customerNote?: string;
  paymentDeadlineAt?: number;
  createdAt: number;
  paymentInstructions?: string;
  items?: OrderItem[];
}

export default function Storefront() {
  const { slug } = useParams<{ slug: string }>();
  const shopSlug = slug || 'demo-shop';
  const { initData, user } = useTelegram();
  const { t, locale, setLocale } = useI18n();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<'catalog' | 'cart' | 'orders'>('catalog');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeOrderId, setActiveOrderId] = useState<string | null>(null);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [viewingReceipt, setViewingReceipt] = useState<any | null>(null);

  // Checkout form
  const [customerName, setCustomerName] = useState(user?.first_name || '');
  const [customerPhone, setCustomerPhone] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [orderNote, setOrderNote] = useState('');

  const cart = useCart(shopSlug);

  // Authenticate with Telegram initData (STO-6)
  useEffect(() => {
    async function auth() {
      const rawInitData = initData || (typeof window !== 'undefined' ? window.Telegram?.WebApp?.initData : '');
      if (!rawInitData) return;
      try {
        const res = await fetch(`${API_ORIGIN}/v1/auth/telegram`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ initData: rawInitData, shopSlug }),
        });
        if (res.ok) {
          const data = await res.json();
          const token = data.accessToken || data.token;
          if (token) {
            setSessionToken(token);
          }
        }
      } catch (err) {
        console.warn('Mini App auth error:', err);
      }
    }
    auth();
  }, [initData, shopSlug]);

  // Fetch shop metadata
  const { data: shop, isLoading: shopLoading } = useQuery<ShopInfo>({
    queryKey: ['shop', shopSlug],
    queryFn: () => storeApiRequest<ShopInfo>(`/shop/${shopSlug}`),
  });

  // Fetch catalog
  const { data: catalog, isLoading: catalogLoading } = useQuery<{
    categories: any[];
    products: Product[];
  }>({
    queryKey: ['catalog', shopSlug],
    queryFn: () => storeApiRequest(`/shop/${shopSlug}/catalog`),
    enabled: !!shop && shop.status !== 'unavailable',
  });

  // Fetch my orders
  const { data: orders = [] } = useQuery<Order[]>({
    queryKey: ['my-orders', shopSlug],
    queryFn: () => storeApiRequest(`/shop/${shopSlug}/orders`),
    enabled: activeTab === 'orders',
  });

  // Fetch active order details
  const { data: activeOrderData } = useQuery<{
    order: Order;
    items: OrderItem[];
    events: any[];
    receipts: any[];
  }>({
    queryKey: ['order-detail', shopSlug, activeOrderId],
    queryFn: () => storeApiRequest(`/shop/${shopSlug}/orders/${activeOrderId}`),
    enabled: !!activeOrderId,
  });

  // Coupon state
  const [couponInput, setCouponInput] = useState(cart.couponCode || '');
  const [couponError, setCouponError] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<{
    code: string;
    discountMinor: number;
    subtotalMinor: number;
    totalMinor: number;
  } | null>(null);

  // Validate coupon mutation
  const validateCouponMutation = useMutation({
    mutationFn: async (code: string) => {
      return storeApiRequest<{
        valid: boolean;
        code: string;
        type: string;
        discountMinor: number;
        subtotalMinor: number;
        totalMinor: number;
      }>(`/shop/${shopSlug}/coupons/validate`, {
        method: 'POST',
        body: JSON.stringify({
          code,
          items: cart.items.map((i) => ({ variantId: i.variantId, quantity: i.quantity })),
        }),
      });
    },
    onSuccess: (data) => {
      setAppliedCoupon({
        code: data.code,
        discountMinor: data.discountMinor,
        subtotalMinor: data.subtotalMinor,
        totalMinor: data.totalMinor,
      });
      cart.setCouponCode(data.code);
      setCouponError('');
    },
    onError: (err: any) => {
      setAppliedCoupon(null);
      cart.setCouponCode('');
      setCouponError(err.message || t('errors.COUPON_INVALID'));
    },
  });

  // Resolve cart item details from catalog products
  const resolveItemDetails = (variantId: string) => {
    if (!catalog?.products) {
      return {
        name: `Item (${variantId.slice(0, 10)})`,
        variantName: null as string | null,
        priceMinor: 0,
        coverImage: undefined as string | undefined,
      };
    }

    for (const p of catalog.products) {
      // 1. Direct match on variant ID
      const variant = p.variants?.find((v) => v.id === variantId);
      if (variant) {
        const isDefaultName =
          !variant.name ||
          variant.name.toLowerCase() === 'default' ||
          variant.name.trim().toLowerCase() === p.name.trim().toLowerCase();
        return {
          product: p,
          variant,
          name: p.name,
          variantName: isDefaultName ? null : variant.name,
          priceMinor: variant.priceMinor ?? p.priceMinor ?? 0,
          coverImage: p.coverImage,
        };
      }

      // 2. Fallback if item.variantId was productId
      if (p.id === variantId) {
        const defaultVar =
          p.variants?.find((v) => v.id === p.defaultVariantId) ||
          p.variants?.find((v) => v.isDefault === 1) ||
          p.variants?.[0];
        const isDefaultName =
          !defaultVar?.name ||
          defaultVar.name.toLowerCase() === 'default' ||
          defaultVar.name.trim().toLowerCase() === p.name.trim().toLowerCase();
        return {
          product: p,
          variant: defaultVar,
          name: p.name,
          variantName: isDefaultName ? null : defaultVar?.name,
          priceMinor: defaultVar?.priceMinor ?? p.priceMinor ?? 0,
          coverImage: p.coverImage,
        };
      }
    }

    return {
      name: `Item (${variantId.slice(0, 10)})`,
      variantName: null as string | null,
      priceMinor: 0,
      coverImage: undefined as string | undefined,
    };
  };

  const getProductCoverImage = (productId?: string, variantId?: string) => {
    if (!catalog?.products) return undefined;
    if (productId) {
      const p = catalog.products.find((prod) => prod.id === productId);
      if (p?.coverImage) return p.coverImage;
    }
    if (variantId) {
      const p = catalog.products.find(
        (prod) => prod.variants?.some((v) => v.id === variantId) || prod.id === variantId
      );
      if (p?.coverImage) return p.coverImage;
    }
    return undefined;
  };

  const getOrderStatusInfo = (status: string) => {
    switch (status) {
      case 'awaiting_payment':
        return {
          label: 'Awaiting Payment',
          classes: 'bg-amber-500/10 text-amber-600 border border-amber-500/20',
        };
      case 'payment_submitted':
        return {
          label: 'Under Review',
          classes: 'bg-sky-500/10 text-sky-600 border border-sky-500/20',
        };
      case 'confirmed':
        return {
          label: 'Confirmed',
          classes: 'bg-brand-10 text-brand-base border border-brand-20',
        };
      case 'fulfilled':
        return {
          label: 'Fulfilled',
          classes: 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20',
        };
      case 'cancelled':
        return {
          label: 'Cancelled',
          classes: 'bg-error/10 text-error border border-error/20',
        };
      default:
        return {
          label: status.replace('_', ' '),
          classes: 'bg-background-tertiary text-foreground-secondary border border-border-secondary',
        };
    }
  };

  const subtotalMinor = cart.items.reduce((sum, item) => {
    const details = resolveItemDetails(item.variantId);
    return sum + (details.priceMinor || 0) * item.quantity;
  }, 0);

  const deliveryFeeMinor = shop?.deliveryFeeMinor || 0;
  const discountMinor = appliedCoupon?.discountMinor || 0;
  const totalMinor = Math.max(0, subtotalMinor + deliveryFeeMinor - discountMinor);

  // Place Order mutation (API-7: Idempotency-Key)
  const placeOrderMutation = useMutation({
    mutationFn: async () => {
      const idempotencyKey = `ord_${crypto.randomUUID()}`;
      return storeApiRequest<Order>(`/shop/${shopSlug}/orders`, {
        method: 'POST',
        headers: {
          'Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify({
          customerName,
          customerPhone,
          addressText: deliveryAddress,
          customerNote: orderNote || undefined,
          items: cart.items.map((i) => ({ variantId: i.variantId, quantity: i.quantity })),
          couponCode: appliedCoupon?.code || cart.couponCode || undefined,
        }),
      });
    },
    onSuccess: (newOrder) => {
      cart.clearCart();
      setAppliedCoupon(null);
      queryClient.invalidateQueries({ queryKey: ['my-orders', shopSlug] });
      setActiveOrderId(newOrder.id);
      setActiveTab('orders');
    },
  });

  // Upload receipt mutation
  const uploadReceiptMutation = useMutation({
    mutationFn: async ({ orderId, file }: { orderId: string; file: File }) => {
      setUploadProgress(true);
      let uploadBlob: Blob = file;
      if (file.type.startsWith('image/')) {
        uploadBlob = await compressImageToBlob(file, { maxDimension: 1600, quality: 0.8 });
      }

      const formData = new FormData();
      formData.append('receipt', uploadBlob, file.name);

      const token = getSessionToken();
      const res = await fetch(`${API_ORIGIN}/v1/shop/${shopSlug}/orders/${orderId}/receipt`, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
      });

      setUploadProgress(false);
      if (!res.ok) {
        throw new Error('Failed to upload receipt');
      }
      return res.json();
    },
    onSuccess: () => {
      setUploadSuccess(true);
      queryClient.invalidateQueries({ queryKey: ['order-detail', shopSlug, activeOrderId] });
    },
  });

  if (shopLoading) {
    return (
      <div className="min-h-screen bg-background-primary flex items-center justify-center p-6">
        <div className="text-center space-y-3">
          <div className="w-6 h-6 border-2 border-brand-base border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-foreground-secondary">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Unavailable Shop Banner (STO-17, README A6)
  if (!shop || shop.status === 'unavailable') {
    return (
      <div className="min-h-screen bg-background-primary flex flex-col items-center justify-center p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-background-tertiary flex items-center justify-center mb-4 text-foreground-tertiary">
          <AlertCircle className="w-6 h-6" />
        </div>
        <h2 className="text-lg font-semibold text-foreground-primary mb-2">
          {t('errors.SHOP_UNAVAILABLE')}
        </h2>
        <p className="text-sm text-foreground-tertiary max-w-sm">
          Please check back later or contact the merchant directly inside Telegram.
        </p>
      </div>
    );
  }

  const filteredProducts = (catalog?.products || []).filter((p) => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  return (
    <div className="min-h-screen bg-background-primary text-foreground-primary flex flex-col pb-20">
      {/* Top Shop Bar */}
      <header className="sticky top-0 z-20 bg-background-primary/90 backdrop-blur border-b border-border-line px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-[6px] bg-brand-10 text-brand-base flex items-center justify-center font-bold text-sm shrink-0">
            {shop.name.slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0">
            <h1 className="text-sm font-semibold truncate text-foreground-primary">{shop.name}</h1>
            <p className="text-[11px] text-foreground-tertiary font-mono">@{shop.slug}</p>
          </div>
        </div>

        {/* Language switch */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setLocale(locale === 'en' ? 'am' : 'en')}
            className="text-xs px-2.5 py-1 rounded-[4px] border border-border-secondary text-foreground-secondary hover:text-foreground-primary transition-colors"
          >
            {locale === 'en' ? 'አማርኛ' : 'EN'}
          </button>
        </div>
      </header>

      {/* Main Body */}
      <main className="flex-1 p-4 max-w-lg mx-auto w-full">
        {/* Tab: CATALOG */}
        {activeTab === 'catalog' && (
          <div className="space-y-4">
            {/* Search Input */}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted" />
              <input
                type="text"
                placeholder={t('storefront.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-background-secondary border border-border-secondary rounded-[6px] pl-9 pr-3 py-2 text-sm text-foreground-primary placeholder:text-foreground-muted focus:outline-none focus:border-brand-base transition-colors"
              />
            </div>

            {/* Categories Chips */}
            {catalog?.categories && catalog.categories.length > 0 && (
              <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
                <button
                  onClick={() => setSelectedCategory('all')}
                  className={`text-xs px-3 py-1.5 rounded-full font-medium whitespace-nowrap transition-colors ${
                    selectedCategory === 'all'
                      ? 'bg-brand-base text-base-white'
                      : 'bg-background-secondary border border-border-secondary text-foreground-secondary hover:text-foreground-primary'
                  }`}
                >
                  {t('storefront.allCategories')}
                </button>
                {catalog.categories.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setSelectedCategory(c.id)}
                    className={`text-xs px-3 py-1.5 rounded-full font-medium whitespace-nowrap transition-colors ${
                      selectedCategory === c.id
                        ? 'bg-brand-base text-base-white'
                        : 'bg-background-secondary border border-border-secondary text-foreground-secondary hover:text-foreground-primary'
                    }`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            )}

            {/* Products Grid */}
            {catalogLoading ? (
              <div className="grid grid-cols-2 gap-3">
                {[1, 2, 3, 4].map((n) => (
                  <div key={n} className="h-44 bg-background-secondary rounded-[6px] animate-pulse" />
                ))}
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="p-8 text-center bg-background-secondary border border-border-line rounded-[6px]">
                <p className="text-sm text-foreground-tertiary">{t('common.empty')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {filteredProducts.map((p) => {
                  const variantId = p.defaultVariantId || p.variants?.[0]?.id || p.id;
                  return (
                    <div
                      key={p.id}
                      onClick={() => cart.addItem(variantId, 1)}
                      className="bg-background-secondary border border-border-line hover:border-brand-20 rounded-[6px] p-3 flex flex-col justify-between cursor-pointer transition-colors shadow-button-sm"
                    >
                      <div className="aspect-square bg-background-tertiary rounded-[4px] mb-2 flex items-center justify-center text-foreground-muted overflow-hidden">
                        <ProductImage
                          src={p.coverImage}
                          alt={p.name}
                          loading="lazy"
                          className="w-full h-full object-cover"
                          fallbackIconClassName="w-8 h-8 opacity-40"
                        />
                      </div>
                      <div>
                        <h3 className="text-xs font-semibold text-foreground-primary truncate">
                          {p.name}
                        </h3>
                        <div className="mt-2 flex items-center justify-between">
                          <span className="text-xs font-mono text-brand-base font-medium">
                            {p.priceMinor !== undefined ? formatMoneyMinor(p.priceMinor) : p.description ? p.description.slice(0, 20) : t('storefront.inStock')}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              cart.addItem(variantId, 1);
                            }}
                            className="p-1.5 rounded-[4px] bg-brand-base text-base-white hover:opacity-90 transition-opacity"
                            title="Add"
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Tab: CART */}
        {activeTab === 'cart' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold tracking-tight">
                {t('storefront.cart')} {cart.totalQuantity > 0 ? `(${cart.totalQuantity})` : ''}
              </h2>
              {cart.items.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    cart.clearCart();
                    setAppliedCoupon(null);
                  }}
                  className="text-xs text-foreground-tertiary hover:text-error transition-colors"
                >
                  {t('common.delete')}
                </button>
              )}
            </div>

            {cart.items.length === 0 ? (
              <div className="p-8 text-center bg-background-secondary border border-border-line rounded-[6px] space-y-3">
                <ShoppingCart className="w-8 h-8 text-foreground-muted mx-auto" />
                <p className="text-sm text-foreground-tertiary">{t('storefront.cartEmpty')}</p>
                <button
                  onClick={() => setActiveTab('catalog')}
                  className="px-4 py-2 text-xs font-medium bg-brand-base text-base-white rounded-[4px]"
                >
                  {t('storefront.catalog')}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Items List */}
                <div className="bg-background-secondary border border-border-line rounded-[6px] divide-y divide-border-line overflow-hidden">
                  {cart.items.map((item) => {
                    const details = resolveItemDetails(item.variantId);
                    const itemTotal = (details.priceMinor || 0) * item.quantity;
                    return (
                      <div key={item.variantId} className="p-3.5 flex gap-3 items-center">
                        {/* Thumbnail */}
                        <div className="w-14 h-14 bg-background-tertiary rounded-[6px] shrink-0 flex items-center justify-center overflow-hidden border border-border-line">
                          <ProductImage
                            src={details.coverImage}
                            alt={details.name}
                            className="w-full h-full object-cover"
                            fallbackIconClassName="w-6 h-6 text-foreground-muted opacity-50"
                          />
                        </div>

                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <h4 className="text-xs font-semibold text-foreground-primary truncate">
                            {details.name}
                          </h4>
                          {details.variantName && (
                            <div className="text-[11px] text-foreground-tertiary truncate">
                              {details.variantName}
                            </div>
                          )}
                          <div className="text-xs font-mono text-foreground-secondary mt-1">
                            {formatMoneyMinor(details.priceMinor, shop.currency)}
                          </div>
                        </div>

                        {/* Quantity Controls & Line Total */}
                        <div className="flex flex-col items-end gap-1.5 shrink-0">
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => cart.updateQuantity(item.variantId, item.quantity - 1)}
                              className="w-6 h-6 flex items-center justify-center rounded-[4px] border border-border-secondary hover:bg-background-tertiary text-foreground-secondary"
                              title="Decrease"
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="text-xs font-mono font-medium w-5 text-center text-foreground-primary">
                              {item.quantity}
                            </span>
                            <button
                              type="button"
                              onClick={() => cart.addItem(item.variantId, 1)}
                              className="w-6 h-6 flex items-center justify-center rounded-[4px] border border-border-secondary hover:bg-background-tertiary text-foreground-secondary"
                              title="Increase"
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => cart.removeItem(item.variantId)}
                              className="w-6 h-6 flex items-center justify-center ml-1 text-foreground-muted hover:text-error transition-colors"
                              title="Remove"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <span className="text-xs font-mono font-medium text-brand-base">
                            {formatMoneyMinor(itemTotal, shop.currency)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Order Summary Breakdown */}
                <div className="bg-background-secondary border border-border-line rounded-[6px] p-3.5 space-y-2">
                  <div className="flex justify-between text-xs text-foreground-secondary">
                    <span>{t('common.subtotal')}</span>
                    <span className="font-mono text-foreground-primary">
                      {formatMoneyMinor(subtotalMinor, shop.currency)}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs text-foreground-secondary">
                    <span>{t('common.deliveryFee')}</span>
                    <span className="font-mono text-foreground-primary">
                      {deliveryFeeMinor > 0 ? formatMoneyMinor(deliveryFeeMinor, shop.currency) : 'Free'}
                    </span>
                  </div>
                  {discountMinor > 0 && (
                    <div className="flex justify-between text-xs text-brand-base font-medium">
                      <span>{t('common.discount')} ({appliedCoupon?.code})</span>
                      <span className="font-mono">-{formatMoneyMinor(discountMinor, shop.currency)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xs font-semibold pt-2 border-t border-border-line text-foreground-primary">
                    <span>{t('common.total')}</span>
                    <span className="font-mono text-brand-base">
                      {formatMoneyMinor(totalMinor, shop.currency)}
                    </span>
                  </div>
                </div>

                {/* Coupon Code Section */}
                <div className="bg-background-secondary border border-border-line rounded-[6px] p-3 space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder={t('storefront.couponPlaceholder')}
                      value={couponInput}
                      onChange={(e) => {
                        setCouponInput(e.target.value.toUpperCase());
                        setCouponError('');
                      }}
                      className="flex-1 bg-background-primary border border-border-secondary rounded-[4px] px-3 py-1.5 text-xs font-mono uppercase text-foreground-primary placeholder:text-foreground-muted focus:outline-none focus:border-brand-base"
                    />
                    <button
                      type="button"
                      disabled={!couponInput.trim() || validateCouponMutation.isPending}
                      onClick={() => validateCouponMutation.mutate(couponInput.trim())}
                      className="px-3 py-1.5 bg-background-tertiary border border-border-secondary hover:border-brand-base text-foreground-primary rounded-[4px] text-xs font-medium transition disabled:opacity-40"
                    >
                      {validateCouponMutation.isPending ? t('common.loading') : t('storefront.applyCoupon')}
                    </button>
                  </div>
                  {couponError && <p className="text-[11px] text-error">{couponError}</p>}
                  {appliedCoupon && (
                    <div className="flex items-center justify-between text-[11px] text-brand-base bg-brand-10 px-2.5 py-1 rounded-[4px]">
                      <span>{t('storefront.couponApplied')}: {appliedCoupon.code}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setAppliedCoupon(null);
                          setCouponInput('');
                          cart.setCouponCode('');
                        }}
                        className="text-foreground-muted hover:text-error ml-2"
                        title="Remove coupon"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Checkout Details Form */}
                <div className="bg-background-secondary border border-border-line rounded-[6px] p-4 space-y-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground-muted font-mono">
                    {t('storefront.customerInfo')}
                  </h3>
                  <div>
                    <label className="block text-xs font-medium text-foreground-secondary mb-1">
                      {t('storefront.fullName')}
                    </label>
                    <input
                      type="text"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      className="w-full bg-background-primary border border-border-secondary rounded-[4px] px-3 py-1.5 text-xs text-foreground-primary focus:outline-none focus:border-brand-base"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-secondary mb-1">
                      {t('storefront.phone')}
                    </label>
                    <input
                      type="tel"
                      placeholder="+2519..."
                      value={customerPhone}
                      onChange={(e) => setCustomerPhone(e.target.value)}
                      className="w-full bg-background-primary border border-border-secondary rounded-[4px] px-3 py-1.5 text-xs text-foreground-primary focus:outline-none focus:border-brand-base"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-secondary mb-1">
                      {t('storefront.deliveryAddress')}
                    </label>
                    <textarea
                      rows={2}
                      value={deliveryAddress}
                      onChange={(e) => setDeliveryAddress(e.target.value)}
                      className="w-full bg-background-primary border border-border-secondary rounded-[4px] px-3 py-1.5 text-xs text-foreground-primary focus:outline-none focus:border-brand-base"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-secondary mb-1">
                      {t('storefront.orderNote')}
                    </label>
                    <input
                      type="text"
                      value={orderNote}
                      onChange={(e) => setOrderNote(e.target.value)}
                      className="w-full bg-background-primary border border-border-secondary rounded-[4px] px-3 py-1.5 text-xs text-foreground-primary focus:outline-none focus:border-brand-base"
                    />
                  </div>
                </div>

                {/* Place Order CTA */}
                <button
                  disabled={!customerPhone || !deliveryAddress || placeOrderMutation.isPending}
                  onClick={() => placeOrderMutation.mutate()}
                  className="w-full py-3 bg-brand-base text-base-white font-medium text-sm rounded-[6px] hover:opacity-90 disabled:opacity-40 transition-opacity flex items-center justify-center gap-2"
                >
                  {placeOrderMutation.isPending
                    ? t('common.loading')
                    : `${t('storefront.placeOrder')} • ${formatMoneyMinor(totalMinor, shop.currency)}`}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Tab: ORDERS */}
        {activeTab === 'orders' && (
          <div className="space-y-4">
            <h2 className="text-base font-semibold tracking-tight">{t('storefront.myOrders')}</h2>

            {/* If looking at active order detail */}
            {activeOrderId && activeOrderData ? (
              <div className="space-y-4">
                {/* Back Button & Status Header */}
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setActiveOrderId(null)}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground-secondary hover:text-foreground-primary py-1 px-1.5 -ml-1.5 rounded-[4px] hover:bg-background-secondary transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    <span>{t('common.back')}</span>
                  </button>
                  <span
                    className={`text-[11px] px-2.5 py-0.5 rounded-full font-medium capitalize ${
                      getOrderStatusInfo(activeOrderData.order.status).classes
                    }`}
                  >
                    {getOrderStatusInfo(activeOrderData.order.status).label}
                  </span>
                </div>

                {/* Order Summary Header Card */}
                <div className="bg-background-secondary border border-border-line rounded-[8px] p-4 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold font-mono text-foreground-primary">
                      Order #{activeOrderData.order.orderNumber}
                    </div>
                    <div className="text-xs text-foreground-tertiary mt-0.5">
                      {new Date(activeOrderData.order.createdAt).toLocaleString()}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] text-foreground-tertiary">{t('common.total')}</div>
                    <div className="text-base font-mono font-bold text-brand-base">
                      {formatMoneyMinor(
                        activeOrderData.order.totalMinor,
                        activeOrderData.order.currency || shop.currency
                      )}
                    </div>
                  </div>
                </div>

                {/* Ordered Products List */}
                <div className="space-y-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground-muted font-mono px-1">
                    {t('storefront.catalog')} ({activeOrderData.items.reduce((s, i) => s + i.quantity, 0)})
                  </h3>
                  <div className="bg-background-secondary border border-border-line rounded-[8px] divide-y divide-border-line overflow-hidden">
                    {activeOrderData.items.map((i) => {
                      const cover = getProductCoverImage(i.productId, i.variantId);
                      const name = i.productName || i.productNameSnapshot || 'Product';
                      const vName = i.variantName || i.variantNameSnapshot;
                      const isNonDefaultVariant =
                        vName && vName.toLowerCase() !== 'default' && vName.trim().toLowerCase() !== name.trim().toLowerCase();
                      return (
                        <div key={i.id} className="p-3.5 flex gap-3 items-center">
                          {/* Thumbnail */}
                          <div className="w-14 h-14 bg-background-tertiary rounded-[6px] shrink-0 flex items-center justify-center overflow-hidden border border-border-line">
                            <ProductImage
                              src={cover}
                              alt={name}
                              className="w-full h-full object-cover"
                              fallbackIconClassName="w-6 h-6 text-foreground-muted opacity-50"
                            />
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <h4 className="text-xs font-semibold text-foreground-primary truncate">
                              {name}
                            </h4>
                            {isNonDefaultVariant && (
                              <div className="text-[11px] text-foreground-tertiary truncate">
                                {vName}
                              </div>
                            )}
                            <div className="text-xs font-mono text-foreground-secondary mt-1">
                              {formatMoneyMinor(i.unitPriceMinor, activeOrderData.order.currency || shop.currency)} × {i.quantity}
                            </div>
                          </div>

                          {/* Line Total */}
                          <div className="text-right shrink-0">
                            <span className="text-xs font-mono font-semibold text-foreground-primary">
                              {formatMoneyMinor(i.lineTotalMinor, activeOrderData.order.currency || shop.currency)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Price Breakdown Card */}
                <div className="bg-background-secondary border border-border-line rounded-[8px] p-3.5 space-y-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground-muted font-mono mb-1">
                    {t('common.total')}
                  </h3>
                  <div className="flex justify-between text-xs text-foreground-secondary">
                    <span>{t('common.subtotal')}</span>
                    <span className="font-mono text-foreground-primary">
                      {formatMoneyMinor(
                        activeOrderData.order.subtotalMinor ??
                          activeOrderData.items.reduce((s, i) => s + i.lineTotalMinor, 0),
                        activeOrderData.order.currency || shop.currency
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs text-foreground-secondary">
                    <span>{t('common.deliveryFee')}</span>
                    <span className="font-mono text-foreground-primary">
                      {(activeOrderData.order.deliveryFeeMinor ?? 0) > 0
                        ? formatMoneyMinor(activeOrderData.order.deliveryFeeMinor!, activeOrderData.order.currency || shop.currency)
                        : 'Free'}
                    </span>
                  </div>
                  {(activeOrderData.order.discountMinor ?? 0) > 0 && (
                    <div className="flex justify-between text-xs text-brand-base font-medium">
                      <span>
                        {t('common.discount')}
                        {activeOrderData.order.couponCode ? ` (${activeOrderData.order.couponCode})` : ''}
                      </span>
                      <span className="font-mono">
                        -{formatMoneyMinor(activeOrderData.order.discountMinor!, activeOrderData.order.currency || shop.currency)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-xs font-semibold pt-2 border-t border-border-line text-foreground-primary">
                    <span>{t('common.total')}</span>
                    <span className="font-mono text-brand-base">
                      {formatMoneyMinor(activeOrderData.order.totalMinor, activeOrderData.order.currency || shop.currency)}
                    </span>
                  </div>
                </div>

                {/* Delivery & Customer Info Card */}
                {(activeOrderData.order.customerNameSnapshot || activeOrderData.order.addressSnapshotText) && (
                  <div className="bg-background-secondary border border-border-line rounded-[8px] p-3.5 space-y-2.5">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground-muted font-mono">
                      {t('storefront.customerInfo')}
                    </h3>
                    <div className="text-xs space-y-1.5 text-foreground-secondary">
                      {activeOrderData.order.customerNameSnapshot && (
                        <div className="flex justify-between">
                          <span className="text-foreground-tertiary">{t('storefront.fullName')}</span>
                          <span className="font-medium text-foreground-primary">
                            {activeOrderData.order.customerNameSnapshot}
                          </span>
                        </div>
                      )}
                      {activeOrderData.order.customerPhoneSnapshot && (
                        <div className="flex justify-between">
                          <span className="text-foreground-tertiary">{t('storefront.phone')}</span>
                          <span className="font-mono text-foreground-primary">
                            {activeOrderData.order.customerPhoneSnapshot}
                          </span>
                        </div>
                      )}
                      {activeOrderData.order.addressSnapshotText && (
                        <div className="pt-1.5 border-t border-border-line/60">
                          <span className="text-foreground-tertiary block mb-0.5">{t('storefront.deliveryAddress')}</span>
                          <span className="text-foreground-primary block">{activeOrderData.order.addressSnapshotText}</span>
                        </div>
                      )}
                      {activeOrderData.order.customerNote && (
                        <div className="pt-1.5 border-t border-border-line/60">
                          <span className="text-foreground-tertiary block mb-0.5">{t('storefront.orderNote')}</span>
                          <span className="text-foreground-secondary italic block">{activeOrderData.order.customerNote}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Uploaded Payment Receipts Section */}
                {activeOrderData.receipts && activeOrderData.receipts.length > 0 && (
                  <div className="bg-background-secondary border border-border-line rounded-[8px] p-3.5 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs font-semibold text-foreground-primary">
                        <Receipt className="w-4 h-4 text-brand-base" />
                        <span>Uploaded Receipt ({activeOrderData.receipts.length})</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {activeOrderData.receipts.map((rcpt: any) => {
                        const isImage = rcpt.mime?.startsWith('image/') || !rcpt.mime?.includes('pdf');
                        const receiptUrl = `${API_ORIGIN}/v1/media/receipts/${rcpt.id}?token=${getSessionToken()}`;
                        return (
                          <div
                            key={rcpt.id}
                            className="p-2.5 bg-background-tertiary/40 border border-border-secondary rounded-[6px] flex items-center justify-between gap-3"
                          >
                            <div
                              onClick={() => setViewingReceipt(rcpt)}
                              className="flex items-center gap-3 min-w-0 cursor-pointer group"
                            >
                              <div className="w-12 h-12 rounded-[4px] bg-background-primary border border-border-line overflow-hidden shrink-0 flex items-center justify-center relative group-hover:border-brand-base transition-colors">
                                {isImage ? (
                                  <>
                                    <img
                                      src={receiptUrl}
                                      alt="Receipt preview"
                                      className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-base-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                      <Eye className="w-4 h-4 text-base-white" />
                                    </div>
                                  </>
                                ) : (
                                  <FileText className="w-6 h-6 text-foreground-muted" />
                                )}
                              </div>
                              <div className="min-w-0">
                                <div className="text-xs font-semibold text-foreground-primary group-hover:text-brand-base transition-colors truncate">
                                  {isImage ? 'Payment Receipt Image' : 'Payment Receipt PDF'}
                                </div>
                                <div className="text-[11px] text-foreground-tertiary">
                                  {new Date(rcpt.created_at).toLocaleString()}
                                </div>
                                {rcpt.reject_reason && (
                                  <div className="text-[11px] text-error mt-0.5">
                                    Reason: {rcpt.reject_reason}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-2 shrink-0">
                              <span
                                className={`text-[10px] px-2 py-0.5 rounded-full font-medium capitalize ${
                                  rcpt.status === 'accepted'
                                    ? 'bg-brand-10 text-brand-base border border-brand-20'
                                    : rcpt.status === 'rejected'
                                    ? 'bg-error/10 text-error border border-error/20'
                                    : 'bg-amber-500/10 text-amber-600 border border-amber-500/20'
                                }`}
                              >
                                {rcpt.status === 'accepted'
                                  ? 'Approved'
                                  : rcpt.status === 'rejected'
                                  ? 'Rejected'
                                  : 'Under Review'}
                              </span>
                              <button
                                type="button"
                                onClick={() => setViewingReceipt(rcpt)}
                                className="p-1 text-foreground-tertiary hover:text-foreground-primary transition-colors"
                                title="View Receipt"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Payment Instructions & Receipt Upload */}
                {activeOrderData.order.status === 'awaiting_payment' && (
                  <div className="bg-background-tertiary/40 border border-border-secondary rounded-[8px] p-3.5 space-y-3">
                    <div className="flex items-center gap-2 text-xs font-semibold text-foreground-primary">
                      <Receipt className="w-4 h-4 text-brand-base" />
                      <span>{t('storefront.paymentInstructions')}</span>
                    </div>
                    <p className="text-xs text-foreground-secondary leading-relaxed">
                      {activeOrderData.order.paymentInstructions ||
                        'Please transfer the exact total to the shop bank account and upload your receipt.'}
                    </p>

                    <div className="space-y-3 pt-2 border-t border-border-secondary">
                      <ImageDropzone
                        value={receiptFile}
                        onChange={(file) => {
                          setReceiptFile(file);
                          setUploadSuccess(false);
                        }}
                        accept="image/jpeg,image/png,image/webp,application/pdf"
                        hint="Click or drag & drop receipt screenshot or PDF (up to 5MB)"
                      />
                      {receiptFile && (
                        <button
                          type="button"
                          disabled={uploadProgress}
                          onClick={() =>
                            uploadReceiptMutation.mutate({
                              orderId: activeOrderData.order.id,
                              file: receiptFile,
                            })
                          }
                          className="w-full py-2 bg-brand-base text-base-white text-xs font-medium rounded-[4px] hover:opacity-90 disabled:opacity-40 transition-opacity"
                        >
                          {uploadProgress ? t('common.loading') : t('storefront.uploadReceipt')}
                        </button>
                      )}
                      {uploadSuccess && (
                        <div className="flex items-center gap-1.5 text-xs text-brand-base">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          <span>{t('storefront.receiptUploaded')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : orders.length === 0 ? (
              <div className="p-8 text-center bg-background-secondary border border-border-line rounded-[6px]">
                <Clock className="w-8 h-8 text-foreground-muted mx-auto mb-2" />
                <p className="text-sm text-foreground-tertiary">{t('common.empty')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {orders.map((o) => {
                  const statusInfo = getOrderStatusInfo(o.status);
                  const itemsCount = o.items ? o.items.reduce((sum, i) => sum + i.quantity, 0) : 0;
                  return (
                    <div
                      key={o.id}
                      onClick={() => setActiveOrderId(o.id)}
                      className="p-3.5 bg-background-secondary border border-border-line hover:border-brand-20 rounded-[8px] space-y-3 cursor-pointer transition-colors shadow-button-sm"
                    >
                      {/* Header */}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-xs font-semibold text-foreground-primary font-mono">
                            Order #{o.orderNumber}
                          </div>
                          <div className="text-[11px] text-foreground-tertiary">
                            {new Date(o.createdAt).toLocaleString()}
                          </div>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium capitalize ${statusInfo.classes}`}>
                          {statusInfo.label}
                        </span>
                      </div>

                      {/* Items Preview */}
                      {o.items && o.items.length > 0 && (
                        <div className="space-y-1.5 pt-2 border-t border-border-line/60">
                          {o.items.map((item) => {
                            const cover = getProductCoverImage(item.productId, item.variantId);
                            const name = item.productName || item.productNameSnapshot || 'Product';
                            const vName = item.variantName || item.variantNameSnapshot;
                            const isNonDefaultVariant =
                              vName && vName.toLowerCase() !== 'default' && vName.trim().toLowerCase() !== name.trim().toLowerCase();
                            return (
                              <div key={item.id} className="flex items-center gap-2.5 text-xs">
                                <div className="w-8 h-8 rounded-[4px] bg-background-tertiary border border-border-line shrink-0 flex items-center justify-center overflow-hidden">
                                  <ProductImage
                                    src={cover}
                                    alt={name}
                                    className="w-full h-full object-cover"
                                    fallbackIconClassName="w-4 h-4 text-foreground-muted opacity-50"
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <span className="font-medium text-foreground-primary truncate block">
                                    {name}
                                  </span>
                                  {isNonDefaultVariant && (
                                    <span className="text-[10px] text-foreground-tertiary truncate block">
                                      {vName}
                                    </span>
                                  )}
                                </div>
                                <span className="font-mono text-foreground-secondary text-[11px] shrink-0">
                                  × {item.quantity}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Footer */}
                      <div className="flex items-center justify-between pt-2 border-t border-border-line">
                        <span className="text-[11px] text-foreground-tertiary">
                          {itemsCount > 0 ? `${itemsCount} ${itemsCount === 1 ? 'item' : 'items'}` : ''}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-mono font-semibold text-foreground-primary">
                            {formatMoneyMinor(o.totalMinor, o.currency || shop.currency)}
                          </span>
                          <ArrowRight className="w-3.5 h-3.5 text-foreground-muted" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Bottom Floating Navigation (Mobile-First 44px touch targets STO-13) */}
      <nav className="fixed bottom-0 left-0 right-0 bg-background-secondary/95 backdrop-blur border-t border-border-line px-6 py-2 flex items-center justify-around z-30">
        <button
          onClick={() => {
            setActiveOrderId(null);
            setActiveTab('catalog');
          }}
          className={`flex flex-col items-center gap-1 min-h-[44px] justify-center px-4 transition-colors ${
            activeTab === 'catalog' ? 'text-brand-base' : 'text-foreground-tertiary hover:text-foreground-primary'
          }`}
        >
          <ShoppingBag className="w-5 h-5" />
          <span className="text-[11px] font-medium">{t('storefront.catalog')}</span>
        </button>

        <button
          onClick={() => setActiveTab('cart')}
          className={`flex flex-col items-center gap-1 min-h-[44px] justify-center px-4 relative transition-colors ${
            activeTab === 'cart' ? 'text-brand-base' : 'text-foreground-tertiary hover:text-foreground-primary'
          }`}
        >
          <div className="relative">
            <ShoppingCart className="w-5 h-5" />
            {cart.totalQuantity > 0 && (
              <span className="absolute -top-1.5 -right-2 bg-brand-base text-base-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                {cart.totalQuantity}
              </span>
            )}
          </div>
          <span className="text-[11px] font-medium">{t('storefront.cart')}</span>
        </button>

        <button
          onClick={() => setActiveTab('orders')}
          className={`flex flex-col items-center gap-1 min-h-[44px] justify-center px-4 transition-colors ${
            activeTab === 'orders' ? 'text-brand-base' : 'text-foreground-tertiary hover:text-foreground-primary'
          }`}
        >
          <FileText className="w-5 h-5" />
          <span className="text-[11px] font-medium">{t('storefront.myOrders')}</span>
        </button>
      </nav>

      {/* Receipt Modal Dialog - Dismissable by clicking outside or pressing Escape */}
      <ModalDialog
        isOpen={!!viewingReceipt}
        onClose={() => setViewingReceipt(null)}
        title="Payment Receipt"
        maxWidthClass="max-w-lg"
      >
        {viewingReceipt && (
          <div className="space-y-4">
            <div className="aspect-auto max-h-[70vh] bg-background-secondary rounded-[6px] overflow-hidden flex items-center justify-center border border-border-line">
              {viewingReceipt.mime?.startsWith('image/') || !viewingReceipt.mime?.includes('pdf') ? (
                <img
                  src={`${API_ORIGIN}/v1/media/receipts/${viewingReceipt.id}?token=${getSessionToken()}`}
                  alt="Receipt Full Preview"
                  className="max-h-[70vh] w-auto max-w-full object-contain"
                />
              ) : (
                <div className="p-8 text-center space-y-3">
                  <FileText className="w-12 h-12 text-foreground-muted mx-auto" />
                  <p className="text-xs text-foreground-secondary">PDF Document</p>
                  <a
                    href={`${API_ORIGIN}/v1/media/receipts/${viewingReceipt.id}?token=${getSessionToken()}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-base text-base-white text-xs font-medium rounded-[4px]"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    <span>Open PDF in new tab</span>
                  </a>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between text-xs text-foreground-secondary pt-2 border-t border-border-line">
              <div>
                Uploaded on {new Date(viewingReceipt.created_at).toLocaleString()}
              </div>
              <a
                href={`${API_ORIGIN}/v1/media/receipts/${viewingReceipt.id}?token=${getSessionToken()}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-brand-base hover:underline font-medium"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                <span>Open original</span>
              </a>
            </div>
          </div>
        )}
      </ModalDialog>
    </div>
  );
}
